/**
 * SmartVariantSelector
 *
 * Enterprise-grade variant picker for the PDP. Drops into any product page
 * that returns `attribute_groups` + `variant_matrix` + `variants` from
 * `/api/products/{slug}` (see /app/VARIANT_ARCHITECTURE.md §5).
 *
 * Behaviours:
 *  • Dependency-aware — e.g. Dimensions only lists values with
 *    `parent_value === selected.size`; re-picks a child default when the
 *    parent changes.
 *  • Disabled-state, not hidden — UNAVAILABLE and OOS options are rendered
 *    with muted styling so customers understand scope (Apple / Lenskart
 *    pattern). The customer's selection is ALWAYS respected — we never
 *    auto-pivot to a different combo. If the picked combo has no SKU, the
 *    Add-to-Cart CTA disables and the picker shows "Out of stock / Not
 *    available" so the shopper can decide.
 *  • Live price — updates from matrix.min_price as the user explores.
 *  • Zero-network — matrix is precomputed server-side; toggles happen in
 *    one React render.
 *  • Three render modes: swatch (colors), pill (sizes/thickness/firmness),
 *    dropdown (long lists like dimensions/gsm).
 *
 * Props:
 *   product          – the /api/products/{slug} response
 *   initialSelection – optional seed (usually the product's first variant)
 *   onVariantChange  – (variant) => void, called whenever the resolved
 *                      variant changes (so the page can swap media, price,
 *                      stock, shipping ETA, etc.)
 *   onAddToCart      – (variant) => void, CTA handler
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Check, AlertCircle, ChevronDown } from "lucide-react";

function classNames(...xs) { return xs.filter(Boolean).join(" "); }

// ─── Dimension unit conversion helpers ────────────────────────────────────
// Canonical dimension values are stored as "WxH" in inches (e.g. "78x60").
// The unit toggle reformats the LABEL only; the underlying canonical value
// sent to the cart / variant matcher never changes.
const DIM_RX = /^(\d{1,3}(?:\.\d+)?)\s*[xX×]\s*(\d{1,3}(?:\.\d+)?)$/;

function parseDim(raw) {
  if (!raw) return null;
  const s = String(raw).replace(/\s+/g, "");
  const m = s.match(DIM_RX);
  if (!m) return null;
  return { w: parseFloat(m[1]), h: parseFloat(m[2]) };
}

function formatDim(raw, unit) {
  const d = parseDim(raw);
  if (!d) return raw;  // non-numeric value, show as-is
  if (unit === "ft") {
    const f = (n) => {
      const v = n / 12;
      return Number.isInteger(v) ? `${v}` : v.toFixed(2).replace(/\.?0+$/, "");
    };
    return `${f(d.w)} ft × ${f(d.h)} ft`;
  }
  if (unit === "cm") {
    const c = (n) => Math.round(n * 2.54);
    return `${c(d.w)} cm × ${c(d.h)} cm`;
  }
  return `${d.w} in × ${d.h} in`;  // default: inches
}

/** Unit toggle used above dimension-like groups (Inches / Feet / Centimeters). */
function UnitToggle({ value, onChange, testId }) {
  const units = [
    { key: "in", label: "Inches" },
    { key: "ft", label: "Feet" },
    { key: "cm", label: "Centimeters" },
  ];
  return (
    <div className="inline-flex rounded-full border border-gray-200 p-0.5 bg-gray-50" data-testid={testId}>
      {units.map((u) => (
        <button
          key={u.key}
          type="button"
          onClick={() => onChange(u.key)}
          data-testid={`${testId}-${u.key}`}
          className={classNames(
            "px-3.5 h-7 text-[12px] font-bold tracking-wide rounded-full transition-all",
            value === u.key
              ? "bg-foreground text-background shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          {u.label}
        </button>
      ))}
    </div>
  );
}

function matchVariant(variants, selection) {
  return variants.find(v => {
    const a = v.attributes || {};
    return Object.entries(selection).every(([k, val]) => (a[k] || "").toString().toLowerCase() === (val || "").toString().toLowerCase());
  });
}

/** Split attribute groups into independent vs dependent for cleaner layout. */
function orderGroups(groups) {
  if (!Array.isArray(groups)) return [];
  // Render each child group directly under its parent (Size → Dimensions
  // → Color), not all roots-then-dependents. This matches how shoppers
  // mentally drill down — pick a size, then refine within that size.
  const byKey = new Map(groups.map(g => [g.key, g]));
  const taken = new Set();
  const out = [];
  const visit = (g) => {
    if (taken.has(g.key)) return;
    taken.add(g.key);
    out.push(g);
    // Append children (groups whose first depends_on is this key) right after
    for (const child of groups) {
      if (child.depends_on && child.depends_on[0] === g.key && byKey.has(child.key)) {
        visit(child);
      }
    }
  };
  // Walk roots first so the original ordering of roots (Size, Color, ...) is preserved.
  for (const g of groups) {
    if (!g.depends_on || g.depends_on.length === 0) visit(g);
  }
  // Catch any orphan dependents whose parent isn't present
  for (const g of groups) if (!taken.has(g.key)) visit(g);
  return out;
}

/** Luxury custom dropdown — replaces native <select> with a polished
 *  popover that supports disabled options, sold-out labels and keyboard nav.
 */
function LuxuryDropdown({ label, value, options, onPick, testId }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    function onDoc(e) { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false); }
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const picked = options.find(o => o.value === value) || null;
  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid={testId}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={classNames(
          "w-full h-12 rounded-xl border-2 px-4 flex items-center justify-between text-sm font-medium transition-all duration-150 bg-white",
          open ? "border-foreground shadow-[0_0_0_4px_rgba(0,0,0,0.05)]" : "border-gray-200 hover:border-gray-400"
        )}
      >
        <span className="truncate">
          {picked ? (
            <>
              <span className="font-semibold">{picked.label}</span>
              {picked.hint && <span className="ml-2 text-muted-foreground font-normal">{picked.hint}</span>}
            </>
          ) : (
            <span className="text-muted-foreground">Select {label}…</span>
          )}
        </span>
        <ChevronDown className={classNames("w-4 h-4 text-muted-foreground transition-transform", open && "rotate-180")} />
      </button>
      {open && (
        <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
             role="listbox" data-testid={`${testId}-menu`}>
          <ul className="max-h-64 overflow-y-auto py-1">
            {options.map(o => {
              const selected = o.value === value;
              const unavailable = o.state === "UNAVAILABLE" || o.state === "PENDING";
              const oos = o.state === "OOS";
              return (
                <li key={o.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    disabled={unavailable}
                    onClick={() => { if (!unavailable) { onPick(o); setOpen(false); } }}
                    data-testid={`${testId}-option-${o.value}`}
                    data-state={o.state}
                    className={classNames(
                      "w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 text-sm transition-colors",
                      selected ? "bg-gray-50" : "hover:bg-gray-50",
                      unavailable && "opacity-40 cursor-not-allowed",
                      oos && "text-muted-foreground"
                    )}
                  >
                    <span className="flex-1 truncate">
                      <span className={classNames("font-medium", selected && "text-foreground")}>{o.label}</span>
                      {o.hint && <span className="ml-2 text-muted-foreground">{o.hint}</span>}
                    </span>
                    {oos && <span className="text-[10px] uppercase tracking-wider text-red-500">Sold out</span>}
                    {unavailable && !oos && <span className="text-[10px] uppercase tracking-wider text-gray-400">N/A</span>}
                    {selected && <Check className="w-4 h-4 text-foreground flex-shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function SmartVariantSelector({ product, initialSelection, onVariantChange, onAddToCart, showPrice = false }) {
  const groups = useMemo(() => orderGroups(product?.attribute_groups || []), [product]);
  const matrix = product?.variant_matrix || {};
  const variants = product?.variants || [];

  // Seed the initial selection from the first available variant unless caller overrode.
  const [selection, setSelection] = useState(() => {
    if (initialSelection) return { ...initialSelection };
    const firstAvail = variants.find(v => (v.warehouse_inventory?.available || 0) > 0) || variants[0];
    return { ...(firstAvail?.attributes || {}) };
  });

  // Unit preference per group key (for dimension-style attrs with unit_toggle=true).
  // Default "in" — admin can swap globally by updating this state.
  const [unitByGroup, setUnitByGroup] = useState({});

  // Fire change callback whenever the resolved variant changes.
  // Notify the host page about the resolved variant (or `null` when the
  // current pick has no SKU). Firing `null` is critical — without it the
  // PDP would silently keep the previous variant and the Add-to-Cart CTA
  // would buy a DIFFERENT product than what the picker shows. The 2nd arg
  // `meta.userInteracted` lets the PDP distinguish "initial state" (don't
  // panic) from "user picked an invalid combo" (show explicit unavailable
  // state instead of falling back to variants[0]).
  const lastVariantIdRef = useRef(null);
  useEffect(() => {
    const matched = matchVariant(variants, selection);
    const newId = matched?.id || null;
    if (newId !== lastVariantIdRef.current) {
      lastVariantIdRef.current = newId;
      onVariantChange?.(matched || null, { userInteracted: userInteractedRef.current });
    }
  }, [selection, variants, onVariantChange]);

  /** Helper: state for a given group/value taking dependencies into account. */
  function stateFor(group, value) {
    // If this group depends on another and the parent isn't selected yet,
    // render as UNAVAILABLE until the parent is picked.
    const deps = group.depends_on || [];
    for (const dep of deps) {
      if (!selection[dep]) return { state: "PENDING", reason: `Pick ${dep} first` };
    }
    // Dependency filtering: skip child values whose parent doesn't match.
    // Case-insensitive to match matchVariant's convention.
    if (deps.length) {
      const parentKey = deps[0];
      const parentVal = selection[parentKey];
      if (!value.parent_value) return { state: "HIDDEN" };                 // orphan
      if (parentVal && String(value.parent_value).toLowerCase() !== String(parentVal).toLowerCase()) {
        return { state: "HIDDEN" };
      }
    }
    // Look up precomputed state from the matrix. If missing, derive fallback.
    const cell = matrix?.[group.key]?.[value.value];
    if (cell) return cell;
    // Derive from variants as fallback
    const trial = { ...selection, [group.key]: value.value };
    const match = variants.find(v => {
      const a = v.attributes || {};
      return Object.entries(trial).every(([k, val]) => (a[k] || "").toLowerCase() === (val || "").toLowerCase());
    });
    if (!match) return { state: "UNAVAILABLE" };
    const avail = (match.warehouse_inventory?.available || 0) > 0;
    return { state: avail ? "AVAILABLE" : "OOS", min_price: match.price_inr, variant_id: match.id };
  }

  const userInteractedRef = useRef(false);

  function handlePick(groupKey, valueObj) {
    const trial = { ...selection };
    trial[groupKey] = valueObj.value;

    // Clear child selections whose dependency just changed.
    // Case-insensitive parent_value comparison so dev data like 'kING'/'king'
    // doesn't silently nuke a valid selection.
    const pickedLower = String(valueObj.value).toLowerCase();
    for (const g of groups) {
      if ((g.depends_on || []).includes(groupKey) && trial[g.key]) {
        const child = (g.values || []).find(v => v.value === trial[g.key]);
        const childParent = child?.parent_value ? String(child.parent_value).toLowerCase() : null;
        if (!child || childParent !== pickedLower) {
          // Auto-pick the first valid child under the new parent, if any.
          const nextChild = (g.values || []).find(v => v.parent_value && String(v.parent_value).toLowerCase() === pickedLower);
          if (nextChild) trial[g.key] = nextChild.value;
          else delete trial[g.key];
        }
      }
    }

    // Always honour the customer's pick — even if the resulting combo has no
    // SKU yet. The picker already paints UNAVAILABLE / OOS states so the
    // shopper can see scope, and the Add-to-Cart CTA is disabled when there
    // is no matching variant. Auto-pivoting to a different combo (which we
    // used to do here) silently overrides the customer's intent and was the
    // main complaint on the storefront — removed in Phase 119.
    userInteractedRef.current = true;
    setSelection(trial);
  }

  const currentVariant = useMemo(() => matchVariant(variants, selection), [variants, selection]);
  const priceINR = currentVariant?.price_inr ?? null;
  const mrpINR = currentVariant?.mrp_inr ?? null;
  const available = currentVariant?.warehouse_inventory?.available ?? 0;
  const discount = mrpINR && priceINR && mrpINR > priceINR
    ? Math.round(((mrpINR - priceINR) / mrpINR) * 100)
    : 0;

  if (!groups.length) return null;

  return (
    <div className="space-y-6" data-testid="smart-variant-selector">
      {/* Live price row — opt-in via showPrice to avoid duplication when the
          host page already renders its own price header. */}
      {showPrice && priceINR != null && (
        <div className="flex items-baseline gap-3" data-testid="variant-live-price">
          <span className="text-3xl font-semibold tracking-tight text-foreground">
            ₹{priceINR.toLocaleString("en-IN")}
          </span>
          {mrpINR > priceINR && (
            <>
              <span className="text-lg line-through text-muted-foreground">₹{mrpINR.toLocaleString("en-IN")}</span>
              <span className="text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                {discount}% OFF
              </span>
            </>
          )}
        </div>
      )}

      {/* Precompute, per axis, the SET of values that actually exist in at
         *  least one variant. Template values not present in ANY variant are
         *  hidden on the storefront so customers never see colors / sizes /
         *  dimensions for which no SKU exists. (Admin-facing data-quality
         *  views still surface the template-vs-variants delta.) */}
      {groups.map((g) => {
        // Set of values that appear in ≥1 variant for THIS axis (case-insensitive).
        const realValues = new Set();
        for (const va of variants) {
          const v = (va.attributes || {})[g.key];
          if (v !== undefined && v !== null && v !== "") realValues.add(String(v).toLowerCase());
        }

        // Axis-level filter: if the axis has 0 or 1 real value, hide the
        // whole selector — nothing to choose. (Single-value axes are still
        // communicated to the shopper via the PDP detail strip.)
        if (realValues.size < 2) return null;

        // Dependency-strict filter (existing) + real-variant filter (new).
        const visible = (g.values || []).filter(v => {
          // Must correspond to an actual variant for this axis
          if (!realValues.has(String(v.value).toLowerCase())) return false;
          // Parent/child dependency constraints
          if (!g.depends_on?.length) return true;
          const parentKey = g.depends_on[0];
          const parentVal = selection[parentKey];
          if (!parentVal) return false;              // parent not picked yet
          if (!v.parent_value) return false;         // orphan → hide
          return String(v.parent_value).toLowerCase() === String(parentVal).toLowerCase();
        });
        if (!visible.length) return null;
        const selectedVal = selection[g.key];
        const unitEnabled = !!g.unit_toggle;
        const unit = unitByGroup[g.key] || "in";
        const formatLabel = (v) => {
          if (unitEnabled) {
            const d = parseDim(v.value);
            if (d) return formatDim(v.value, unit);
          }
          return v.label || v.value;
        };
        return (
          <div key={g.key} data-testid={`attr-group-${g.key}`}>
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <span className="text-[13px] uppercase tracking-[0.12em] font-extrabold text-gray-900" data-testid={`attr-label-${g.key}`}>
                {g.label}
              </span>
              <div className="flex items-center gap-3">
                {unitEnabled && (
                  <UnitToggle
                    value={unit}
                    onChange={(u) => setUnitByGroup(prev => ({ ...prev, [g.key]: u }))}
                    testId={`unit-toggle-${g.key}`}
                  />
                )}
                {selectedVal && (
                  <span className="text-sm text-foreground" data-testid={`attr-selected-${g.key}`}>
                    {(() => {
                      const picked = g.values.find(v => v.value === selectedVal);
                      return picked ? formatLabel(picked) : selectedVal;
                    })()}
                  </span>
                )}
              </div>
            </div>

            {/* Render mode: swatch | image_circle | radio_grid | pill | dropdown */}
            {/* COLOR axis override — show large oval per-variant image swatches with label */}
            {(() => {
              const labelLower = String(g.label || g.key || "").toLowerCase();
              const isColorAxis = /\bcolou?r\b|\bshade\b|\bfinish\b/.test(labelLower);
              if (!isColorAxis) return null;

              // For each color value, derive the swatch image from the FIRST
              // matching variant's media (variant.image_url || variant.media[0].url).
              // This matches the old ProductPage.js fallback behaviour so each
              // color shows the actual product photo of that colour, not a hex
              // fill. v.image_url (registry-level) is used only as a final
              // backstop.
              const variantImageFor = (val) => {
                const matches = variants.filter(va => {
                  const aval = (va.attributes || {})[g.key];
                  return String(aval || "").toLowerCase() === String(val || "").toLowerCase();
                });
                for (const va of matches) {
                  const url = va.image_url || va.media?.[0]?.url || va.media?.[0]?.image_url;
                  if (url) return url;
                }
                return null;
              };

              return (
                <div className="flex flex-wrap gap-3" data-testid={`swatch-row-${g.key}`}>
                  {visible.map(v => {
                    const { state } = stateFor(g, v);
                    const isPicked = selectedVal === v.value;
                    const dim = state === "UNAVAILABLE" || state === "PENDING";
                    const swatchImage = v.image_url || variantImageFor(v.value);
                    const lightBg = v.hex && ["#FFFFFF", "#F7EDD8", "#F5E6C8", "#DBC89A"].includes(v.hex.toUpperCase());
                    return (
                      <button
                        key={v.value}
                        type="button"
                        onClick={() => handlePick(g.key, v)}
                        title={v.label + (state === "OOS" ? " — Sold Out" : state === "UNAVAILABLE" ? " — Not available" : "")}
                        data-testid={`swatch-${g.key}-${v.value}`}
                        data-state={state}
                        disabled={dim}
                        aria-disabled={dim}
                        className={classNames(
                          "relative flex flex-col items-center gap-1.5 transition-all focus:outline-none",
                          isPicked ? "scale-105" : !dim && "hover:scale-105",
                          dim && "opacity-30 cursor-not-allowed",
                          state === "OOS" && "opacity-60",
                        )}
                      >
                        <span
                          className={classNames(
                            "block w-16 h-20 rounded-[40%] overflow-hidden border-2 transition-all relative",
                            isPicked
                              ? "border-[#FFB800] shadow-[0_0_0_2px_rgba(255,184,0,0.25)]"
                              : "border-gray-200 hover:border-gray-400",
                            lightBg && !swatchImage && "border-gray-300",
                          )}
                          style={{
                            backgroundColor: swatchImage ? undefined : (v.hex || "#F5F5F5"),
                            backgroundImage: swatchImage ? `url(${swatchImage})` : undefined,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                        >
                          {state === "OOS" && (
                            <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <span className="w-0.5 h-[140%] bg-red-500/70 rotate-45 absolute" />
                            </span>
                          )}
                          {v.badge && (
                            <span
                              className="absolute -top-1.5 left-1/2 -translate-x-1/2 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full text-white shadow-md whitespace-nowrap"
                              style={{ backgroundColor: v.badge_color || "#3B82F6" }}
                              data-testid={`swatch-badge-${g.key}-${v.value}`}
                            >
                              {v.badge}
                            </span>
                          )}
                        </span>
                        <span
                          className={classNames(
                            "text-[10px] font-medium leading-tight text-center max-w-[72px] truncate",
                            isPicked ? "text-gray-900" : "text-gray-500",
                          )}
                        >
                          {v.label || v.value}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })() || (g.display_as === "swatch" ? (
              <div className="flex flex-wrap gap-2.5" data-testid={`swatch-row-${g.key}`}>
                {visible.map(v => {
                  const { state } = stateFor(g, v);
                  const isPicked = selectedVal === v.value;
                  const dim = state === "UNAVAILABLE" || state === "PENDING";
                  const lightBg = v.hex && ["#FFFFFF", "#F7EDD8", "#F5E6C8", "#DBC89A"].includes(v.hex.toUpperCase());
                  return (
                    <button
                      key={v.value}
                      type="button"
                      onClick={() => handlePick(g.key, v)}
                      title={v.label + (state === "OOS" ? " — Sold Out" : state === "UNAVAILABLE" ? " — Not available" : "")}
                      data-testid={`swatch-${g.key}-${v.value}`}
                      data-state={state}
                      disabled={dim}
                      aria-disabled={dim}
                      className={classNames(
                        "relative h-12 w-12 rounded-full transition-all duration-200 focus:outline-none",
                        isPicked
                          ? "ring-2 ring-offset-2 ring-foreground scale-105"
                          : "ring-1 ring-inset ring-gray-200 hover:ring-gray-400 hover:scale-105",
                        dim && "opacity-30 cursor-not-allowed !scale-100",
                        state === "OOS" && "opacity-60",
                        lightBg && "border border-gray-200",
                      )}
                      style={{
                        backgroundColor: v.hex || "#EEE",
                        backgroundImage: v.image_url ? `url(${v.image_url})` : undefined,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        boxShadow: isPicked ? "inset 0 0 0 2px rgba(255,255,255,0.85)" : "inset 0 1px 2px rgba(0,0,0,0.08)",
                      }}
                    >
                      {isPicked && (
                        <Check className={classNames(
                          "absolute inset-0 m-auto w-4 h-4",
                          lightBg ? "text-foreground" : "text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]",
                        )} strokeWidth={3} />
                      )}
                      {state === "OOS" && (
                        <span className="absolute inset-0 rounded-full flex items-center justify-center">
                          <span className="w-0.5 h-[140%] bg-red-500/70 rotate-45 absolute" />
                        </span>
                      )}
                      {v.badge && (
                        <span
                          className="absolute -top-1.5 -right-1.5 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full text-white shadow-md whitespace-nowrap"
                          style={{ backgroundColor: v.badge_color || "#3B82F6" }}
                          data-testid={`swatch-badge-${g.key}-${v.value}`}
                        >
                          {v.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : g.display_as === "image_circle" ? (
              <div className="flex flex-wrap gap-4" data-testid={`image-circle-row-${g.key}`}>
                {visible.map(v => {
                  const { state } = stateFor(g, v);
                  const isPicked = selectedVal === v.value;
                  const dim = state === "UNAVAILABLE" || state === "PENDING";
                  return (
                    <button
                      key={v.value}
                      type="button"
                      onClick={() => handlePick(g.key, v)}
                      title={v.label + (state === "OOS" ? " — Sold Out" : "")}
                      data-testid={`image-circle-${g.key}-${v.value}`}
                      data-state={state}
                      disabled={dim}
                      aria-disabled={dim}
                      className={classNames(
                        "group flex flex-col items-center gap-1.5 focus:outline-none",
                        dim && "opacity-30 cursor-not-allowed",
                        state === "OOS" && "opacity-60",
                      )}
                    >
                      <span className="relative">
                        <span
                          className={classNames(
                            "block h-14 w-14 rounded-full overflow-hidden transition-all duration-200",
                            isPicked
                              ? "ring-2 ring-offset-2 ring-foreground"
                              : "ring-1 ring-gray-200 group-hover:ring-gray-400 group-hover:scale-105",
                          )}
                          style={{
                            backgroundColor: v.hex || "#F1F1F1",
                            backgroundImage: v.image_url ? `url(${v.image_url})` : undefined,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                          }}
                        />
                        {state === "OOS" && (
                          <span className="absolute inset-0 rounded-full flex items-center justify-center">
                            <span className="w-0.5 h-[140%] bg-red-500/70 rotate-45 absolute" />
                          </span>
                        )}
                        {v.badge && (
                          <span
                            className="absolute -top-2 left-1/2 -translate-x-1/2 text-[9px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full text-white shadow-md whitespace-nowrap"
                            style={{ backgroundColor: v.badge_color || "#3B82F6" }}
                            data-testid={`image-circle-badge-${g.key}-${v.value}`}
                          >
                            {v.badge}
                          </span>
                        )}
                      </span>
                      <span className={classNames("text-[12px] font-bold tracking-tight", isPicked ? "text-foreground" : "text-gray-700")}>
                        {v.label || v.value}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : g.display_as === "radio_grid" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 rounded-xl border border-gray-100 bg-gray-50/60 p-3" data-testid={`radio-grid-${g.key}`}>
                {visible.map(v => {
                  const { state } = stateFor(g, v);
                  const isPicked = selectedVal === v.value;
                  const unavail = state === "UNAVAILABLE" || state === "PENDING";
                  return (
                    <button
                      key={v.value}
                      type="button"
                      onClick={() => handlePick(g.key, v)}
                      disabled={unavail}
                      aria-disabled={unavail}
                      data-testid={`radio-${g.key}-${v.value}`}
                      data-state={state}
                      className={classNames(
                        "flex items-center gap-2 px-3 h-10 rounded-lg text-left text-[15px] font-bold tracking-tight transition-all",
                        isPicked
                          ? "bg-white ring-2 ring-foreground shadow-sm"
                          : unavail
                            ? "opacity-40 cursor-not-allowed"
                            : state === "OOS"
                              ? "bg-white hover:bg-gray-100 text-muted-foreground"
                              : "bg-white hover:bg-gray-100",
                      )}
                    >
                      <span
                        className={classNames(
                          "w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                          isPicked ? "border-foreground" : "border-gray-300",
                        )}
                      >
                        {isPicked && <span className="w-2 h-2 rounded-full bg-foreground" />}
                      </span>
                      <span className="truncate flex-1">{formatLabel(v)}</span>
                      {state === "OOS" && <span className="text-[9px] uppercase tracking-wide text-red-500">Sold out</span>}
                      {v.badge && (
                        <span
                          className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full text-white"
                          style={{ backgroundColor: v.badge_color || "#3B82F6" }}
                        >
                          {v.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ) : g.display_as === "dropdown" ? (
              <LuxuryDropdown
                label={g.label}
                value={selectedVal}
                testId={`dropdown-${g.key}`}
                options={visible.map(v => {
                  const { state } = stateFor(g, v);
                  return { value: v.value, label: formatLabel(v), hint: v.short_label, state };
                })}
                onPick={(opt) => handlePick(g.key, { value: opt.value, label: opt.label })}
              />
            ) : (
              // Pills — respect display_as='pill' regardless of count (wrap and scroll).
              <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto pr-1" data-testid={`pill-row-${g.key}`}>
                {visible.map(v => {
                  const { state } = stateFor(g, v);
                  const isPicked = selectedVal === v.value;
                  const base = "relative px-4 h-11 min-w-[3.5rem] rounded-xl border-2 text-[15px] font-bold tracking-tight transition-all duration-200";
                  const styles = isPicked
                    ? "bg-foreground text-background border-foreground shadow-[0_2px_8px_rgba(0,0,0,0.15)]"
                    : state === "UNAVAILABLE" || state === "PENDING"
                      ? "border-gray-100 text-gray-300 cursor-not-allowed bg-white line-through decoration-gray-300"
                      : state === "OOS"
                        ? "border-gray-200 text-gray-500 bg-gray-50 hover:border-gray-300"
                        : "bg-white border-gray-200 text-foreground hover:border-foreground hover:shadow-[0_2px_6px_rgba(0,0,0,0.06)]";
                  return (
                    <button
                      key={v.value}
                      type="button"
                      onClick={() => handlePick(g.key, v)}
                      disabled={state === "UNAVAILABLE" || state === "PENDING"}
                      aria-disabled={state === "UNAVAILABLE" || state === "PENDING"}
                      data-testid={`pill-${g.key}-${v.value}`}
                      data-state={state}
                      className={classNames(base, styles)}
                    >
                      {formatLabel(v)}
                      {state === "OOS" && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-red-500">· Sold out</span>}
                      {v.badge && (
                        <span
                          className="absolute -top-2 -right-2 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full text-white shadow-md"
                          style={{ backgroundColor: v.badge_color || "#3B82F6" }}
                        >
                          {v.badge}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        );
      })}

      {/* Combo banner */}
      {currentVariant ? (
        available > 0 ? (
          <div className="flex items-center gap-2 text-xs text-emerald-700" data-testid="variant-stock-ok">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
            In stock · ready to ship{available < 6 ? ` — only ${available} left` : ""}
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-amber-700" data-testid="variant-stock-oos">
            <AlertCircle className="w-3.5 h-3.5" />
            Sold out — tap other options or use "Notify me"
          </div>
        )
      ) : (() => {
        const missingGroup = groups.find(g => !selection[g.key]);
        if (missingGroup) {
          return (
            <div className="text-xs text-muted-foreground" data-testid="variant-needs-pick">
              Select {missingGroup.label} to see availability
            </div>
          );
        }
        // All axes filled but no SKU exists — Shopify/Amazon UX: explicit
        // "this combo isn't available" so the customer doesn't think the
        // page is broken. Keep this concise — the second helper line below
        // already tells them what to do (pick a different size/colour).
        return (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex gap-2" data-testid="variant-not-available">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <div className="font-semibold">This combination isn't available right now.</div>
          </div>
        );
      })()}

      {/* CTA — internal, only rendered when caller passes onAddToCart.
          The PDP renders its own (richer) CTA + Notify-Me block, so this
          is mainly used by the quick-view modal and tests. */}
      {onAddToCart && (
        <button
          type="button"
          disabled={!currentVariant || available <= 0}
          onClick={() => onAddToCart(currentVariant)}
          data-testid="smart-variant-atc"
          className={classNames(
            "w-full h-12 rounded-lg font-semibold tracking-wide uppercase text-sm transition-colors",
            currentVariant && available > 0
              ? "bg-foreground text-background hover:opacity-90"
              : "bg-gray-200 text-gray-500 cursor-not-allowed"
          )}
        >
          {!currentVariant
            ? (groups.every(g => selection[g.key]) ? "Combination unavailable" : "Select options")
            : available > 0
              ? "Add to Cart"
              : "Sold out — see Notify me"}
        </button>
      )}
    </div>
  );
}
